import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import { useCurrency } from '../../../../hooks/useCurrency';

const PartialSalesSection = ({ user, company }) => {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();
  // Helper function to format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';

    if (num >= 1000000) {
      return `${currencySym}${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${currencySym}${(num / 1000).toFixed(0)}K`;
    }
    return `${currencySym}${num.toLocaleString()}`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString();
  };

  const partialSaleListings = user.partial_sale_listings || [];
  const openPartialSaleListings = user.open_partial_sale_listings || [];
  
  // Combine both types of listings and remove duplicates based on ID
  const allListingsWithDuplicates = [...partialSaleListings, ...openPartialSaleListings];
  const allListings = allListingsWithDuplicates.filter((listing, index, self) =>
    index === self.findIndex((l) => l.id === listing.id)
  );

  if (allListings.length === 0) {
    return null;
  }

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <h3 className="text-xl font-semibold text-succession-text-primary mb-4">
        {intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.partial_sale_listings' })}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allListings.map((listing, index) => (
          <div key={listing.id || index} className="border border-succession-border rounded-lg p-3">
          {/* Status and Price Header */}
          <div className="flex justify-between items-start mb-3">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                listing.sale_status === 'active' ? 'bg-green-100 text-green-800' :
                listing.sale_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {listing.sale_status ? listing.sale_status.charAt(0).toUpperCase() + listing.sale_status.slice(1) : 'Listed'}
              </span>
              
              {/* User's Transition Goals */}
              {user.transition_goal_names && user.transition_goal_names.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {user.transition_goal_names.slice(0, 2).map((goal, goalIndex) => (
                    <span
                      key={goalIndex}
                      className="inline-flex px-1.5 py-0.5 text-xs rounded-full bg-succession-primary-bg text-succession-primary border border-succession-primary transition-colors duration-300"
                    >
                      {goal}
                    </span>
                  ))}
                  {user.transition_goal_names.length > 2 && (
                    <span className="inline-flex px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      +{user.transition_goal_names.length - 2}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-succession-text-muted">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.asking_price' })}</div>
              <div className="text-base font-bold text-succession-text-primary">
                {(!listing.asking_price || listing.asking_price === 0 || listing.asking_price === '0' || listing.asking_price === '0.0' || parseFloat(listing.asking_price) === 0) ? intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.negotiable' }) : formatCurrency(listing.asking_price)}
              </div>
            </div>
          </div>

          {/* Headline */}
          {listing.segment_details && (
            <div className="mb-2">
              <h5 className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.headline_label' })}</h5>
              <p className="text-sm font-semibold text-succession-text-primary line-clamp-2">
                {listing.segment_details}
              </p>
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <div className="mb-2">
              <h5 className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.description_label' })}</h5>
              <p className="text-sm font-semibold text-succession-text-primary line-clamp-2">
                {listing.description}
              </p>
            </div>
          )}

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {listing.assets_under_management !== undefined && listing.assets_under_management !== null && (
              <div>
                <div className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.aum_label' })}</div>
                <div className="text-sm font-semibold text-succession-text-primary">
                  {formatCurrency(listing.assets_under_management)}
                </div>
              </div>
            )}
            
            {listing.number_of_clients !== undefined && listing.number_of_clients !== null && (
              <div>
                <div className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.clients_label' })}</div>
                <div className="text-sm font-semibold text-succession-text-primary">
                  {formatNumber(listing.number_of_clients)}
                </div>
              </div>
            )}
            
            {listing.fee_based_revenue !== undefined && listing.fee_based_revenue !== null && (
              <div>
                <div className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.fee_based_revenue' })}</div>
                <div className="text-sm font-semibold text-succession-text-primary">
                  {formatCurrency(listing.fee_based_revenue)}
                </div>
              </div>
            )}
            
            {listing.trails_revenue !== undefined && listing.trails_revenue !== null && (
              <div>
                <div className="text-xs text-succession-text-muted mb-0.5">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.trails_revenue' })}</div>
                <div className="text-sm font-semibold text-succession-text-primary">
                  {formatCurrency(listing.trails_revenue)}
                </div>
              </div>
            )}
          </div>

          {/* Interest Status (without view/interest counts) */}
          {listing.current_user_has_expressed_interest && (
            <div className="flex items-center gap-1 text-xs text-succession-primary font-medium pt-2 border-t border-succession-border">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              <span>{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.interested' })}</span>
            </div>
          )}
        </div>
        ))}
      </div>

      {/* Partial Sales Marketplace Link */}
      <div className="mt-4 bg-succession-primary/10 border border-succession-primary/20 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.interested_in_partial_sale' })}</h5>
        <p className="text-sm text-succession-text-secondary mb-2">
          {intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.visit_marketplace' })}
        </p>
        <a
          href="/succession/partial_sales"
          className="text-succession-primary hover:underline text-sm font-medium"
        >
          {intl.formatMessage({ id: 'succession.profile_drawer.partial_sales.go_to_marketplace' })}
        </a>
      </div>
    </div>
  );
};

PartialSalesSection.propTypes = {
  user: PropTypes.shape({
    partial_sale_listings: PropTypes.arrayOf(PropTypes.object),
    open_partial_sale_listings: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  company: PropTypes.object,
};

export default PartialSalesSection;

